import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import * as Api from '@ViewModels';
import { useEventLogging } from '../../../../models/Logging';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { IMrryMessage, MrryDirection, MrryMessageType, MrryModal } from '../../../components/MrryModal';
import { useErrorMessages } from '../../../hooks';
import { useCoffeeLeadIssueCompleteMutation, useCoffeeLeadIssueExportMutation } from '../../../queries/CoffeeLeadIssue';
import { BasicPage } from '../../BasicPage';
import { ListHeader } from './components/ListHeader';
import { ListItemsContainer } from './components/ListItemsContainer';
import { useIssuesLog } from './hooks';
import { styleSheet } from './styles';

export type CompletionStatus = '' | 'incomplete' | 'complete';

export interface IIssuesLogFilter {
	category?: Api.CoffeeLeadIssueCategory;
	completionStatus?: CompletionStatus;
	date?: Api.IRange<Date>;
	user?: Api.IUser;
}

const issuesLogFilterToCoffeeLeadIssueFilter = (filter: IIssuesLogFilter): Api.ICoffeeLeadIssueFilter => {
	const newFilter: Api.ICoffeeLeadIssueFilter = {
		criteria: [],
		op: Api.FilterOperator.And,
	};
	if (filter?.date) {
		newFilter.criteria.push({
			op: Api.FilterOperator.Gt,
			property: Api.CoffeeLeadIssueFilterCriteriaProperty.CreationDate,
			value: moment.utc(filter.date.start).format('YYYY-MM-DD'),
		});
		newFilter.criteria.push({
			op: Api.FilterOperator.Lt,
			property: Api.CoffeeLeadIssueFilterCriteriaProperty.CreationDate,
			value: moment.utc(filter.date.end).add(1, 'day').format('YYYY-MM-DD'),
		});
	}
	if (filter?.completionStatus === 'incomplete') {
		newFilter.criteria.push({
			property: Api.CoffeeLeadIssueFilterCriteriaProperty.IsComplete,
			value: 'false',
		});
	}
	if (filter?.completionStatus === 'complete') {
		newFilter.criteria.push({
			property: Api.CoffeeLeadIssueFilterCriteriaProperty.IsComplete,
			value: 'true',
		});
	}
	if (filter?.user?.id) {
		newFilter.criteria.push({
			property: Api.CoffeeLeadIssueFilterCriteriaProperty.User,
			value: filter.user.id,
		});
	}
	if (filter?.category) {
		newFilter.criteria.push({
			property: Api.CoffeeLeadIssueFilterCriteriaProperty.Category,
			value: filter.category,
		});
	}
	return newFilter;
};

export const IssuesLog: React.FC = () => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('IssuesLog');

	const [loading, setLoading] = React.useState<boolean>(true);
	const [filterState, setFilter] = useState<IIssuesLogFilter>(null);
	const [mrryMessage, setMrryMessage] = useState<IMrryMessage>(null);

	const { getItems, items, setItems } = useIssuesLog(userSession);

	const markCompleteMutation = useCoffeeLeadIssueCompleteMutation({
		onSuccess: (updatedIssue, issueId) => {
			const nextItems = [...(items || [])];
			const index = nextItems.findIndex(x => x.id === issueId);
			if (index >= 0) {
				nextItems.splice(index, 1, updatedIssue);
				setItems(nextItems);
			}
			setMrryMessage({ content: 'Issue marked as complete.', type: MrryMessageType.Success });
		},
		onError: error => {
			logApiError('MarkComplete-Error', error);
			errorMessages.push({ messages: ['Error when trying to mark issue as complete.'] });
		},
	});

	const exportMutation = useCoffeeLeadIssueExportMutation({
		onSuccess: () => {
			setMrryMessage({
				content: 'CSV exported successfully, you should receive an email with the file.',
				type: MrryMessageType.Success,
			});
		},
		onError: error => {
			logApiError('CsvExport-Error', error);
			errorMessages.push({ messages: ['Error when trying to export CSV, please try again.'] });
		},
	});

	const getMoreItems = React.useCallback(async () => {
		try {
			await getItems(
				{
					pageSize: 25,
					sort: 'desc',
				},
				issuesLogFilterToCoffeeLeadIssueFilter(filterState)
			);
		} catch (error) {
			logApiError('LoadIssues-Error', error);
			errorMessages.pushApiError(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterState]);

	React.useEffect(() => {
		setLoading(true);
		getMoreItems()?.finally(() => {
			setLoading(false);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterState]);

	const handleFilterChange = (filter: IIssuesLogFilter) => {
		setFilter(filter);
	};

	const handleExportRequest = () => {
		exportMutation.mutate(issuesLogFilterToCoffeeLeadIssueFilter(filterState));
	};

	const handleMarkComplete = (issueId: string) => {
		markCompleteMutation.mutate(issueId);
	};

	return (
		<BasicPage appearance='light' className={`${css(styleSheet.container)} issues-log`} title='Issues Log'>
			<div className={css(styleSheet.contentWrapper)}>
				<ListHeader
					exportingCsv={exportMutation.isPending}
					onChange={handleFilterChange}
					onExport={handleExportRequest}
				/>
				<ListItemsContainer isLoading={loading} items={items} onMarkComplete={handleMarkComplete} />
				<Waypoint bottomOffset='-200px' onEnter={getMoreItems} />
			</div>
			{!!mrryMessage && (
				<MrryModal
					fromDirection={MrryDirection.FromTop}
					message={mrryMessage}
					onRequestClose={() => setMrryMessage(null)}
				/>
			)}
		</BasicPage>
	);
};
